import React, { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import GetSpace from "../../../utils/space"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faBellConcierge, faClose, faFile, faFileSignature, faMoneyBill } from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useRegister } from "../../../hooks/Register/useRegister"
import { useError } from "../../../context/useErrorContext"
import { ExtraServiceEntity } from "../../../Objects/ExtraService/ExtraServiceEntity"
import { useGetExtraService } from "../../../hooks/ExtraService/useGetExtraService"
import { useSaveExtraService } from "../../../hooks/ExtraService/useSaveExtraService"
import { useCreateExtraService } from "../../../hooks/ExtraService/useCreateExtraService"
import { useToggle } from "../../../hooks/useToggle"
import { ToggleComponet } from "../../../components/Toggle/ToggleComponet"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import { useRemoveExtraService } from "../../../hooks/ExtraService/useRemoveExtraService"
import { EMoneyConditionType } from "../../../Enum/EMoneyConditionType"
import { EAutoType } from "../../../Enum/EAutoType"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface ExtraServicePupopProbs {
  title: string
  extraService?: ExtraServiceEntity
  onClonse: () => void
  onCopy?: () => void
}

export const ExtraServicePupop: React.FC<ExtraServicePupopProbs> = ({ extraService, title, onClonse, onCopy }) => {
  const { t } = useTranslation("extraService")

  const isCopiedDraft = extraService?.id === -1

  const canEdite = !usePermission(Permissions.CanEditeExtraService)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { handleGetExtraService } = useGetExtraService()

  const { setNewConfirmation } = useConfirmation()

  const { handleRemove } = useRemoveExtraService()

  const {
    name,
    description,
    status,
    value,
    conditionType,
    setConditionType,
    handleNameChange,
    handleCoastChange,
    handleDescriptionChange,
    setIsAutomatic,
    handleStatusChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreateExtraService()

  const { handleSavesExtraService } = useSaveExtraService()

  const { setError } = useError()

  const valuesToggle = Object.mapEnumToStrings(EMoneyConditionType)

  const { selectedType, handleToggleSelection } = useToggle(
    extraService?.conditionType === EMoneyConditionType.FixedValue ? [valuesToggle[0]] : [valuesToggle[1]],
  )

  const handleChangeType = (e: string[]) => {
    setConditionType(e[0] === valuesToggle[0] ? EMoneyConditionType.FixedValue : EMoneyConditionType.Percentage)
  }

  const valuesToggleMode = Object.mapEnumToStrings(EAutoType)

  const { selectedType: selectedModeType, handleToggleSelection: handleToggleModeSelection } = useToggle(
    extraService?.isAutomatic === true ? [valuesToggleMode[0]] : [valuesToggleMode[1]],
  )

  const handleChangeModeType = (e: string[]) => {
    setIsAutomatic(e[0] === valuesToggleMode[0] ? EAutoType.Auto : EAutoType.NotAuto)
  }

  useEffect(() => {
    handleInitialValue(extraService)
  }, [extraService])

  const handleTrySubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const result = handleSubmitForm()

    if ("errors" in result) {
      const errorMessages = result.errors.map((e) => e.message).join("\n")

      setError(t("general:label_invalid"), errorMessages)

      return
    }

    if (!Number.isFinite(result.valueBase) || result.valueBase <= 0) {
      const message =
        conditionType === EMoneyConditionType.Percentage
          ? t("label_percentage_value_cannot_be_negative")
          : t("label_fixed_value_cannot_be_negative")

      setError(t("general:label_invalid"), message)

      return
    }

    handleSavesExtraService(result, () => {
      onClonse()
      handleGetExtraService(() => {})
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faBellConcierge}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <>
                    {extraService && extraService.id !== -1 && (
                      <div style={{ width: "100px", marginRight: "5px" }}>
                        <GenericDropDown
                          data={Object.mapEnumToStrings(EEnableStatusType, {
                            exclude: [EEnableStatusType.All],
                          })}
                          disable={canEdite}
                          initialState={status}
                          handlerFuncion={handleStatusChange}
                          descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.select.select"
                        />

                        <GetSpace right={10} />
                      </div>
                    )}

                    <PopupHelpButton
                      helpKey="pages.serivesdir.extraservices.extraservicepupop.1"
                      variant="global"
                      title={t("helpPopup:global.extraService.title")}
                      description={t("helpPopup:global.extraService.description")}
                      bullets={t("helpPopup:global.extraService.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClonse}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.button.onClonse"
                    />
                  </>
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form onSubmit={handleTrySubmit}>
              <section className="p-2">
                <ToggleComponet
                  canSelectType={true}
                  selectedType={selectedType}
                  valuesToggle={valuesToggle}
                  handleChangeType={handleChangeType}
                  disable={canEdite}
                  handleToggleSelection={handleToggleSelection}
                  descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.toggle.toggle"
                />

                <GetSpace top={5} />

                <ToggleComponet
                  canSelectType={true}
                  selectedType={selectedModeType}
                  valuesToggle={valuesToggleMode}
                  disable={canEdite}
                  handleChangeType={handleChangeModeType}
                  handleToggleSelection={handleToggleModeSelection}
                  descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.toggle.toggle2"
                />

                <div className={defaultStyle.divisionBar}></div>

                <>
                  <InputField
                    type="text"
                    icon={faFileSignature}
                    value={name}
                    placeholder={t("label_name_place_holder")}
                    onInvalidMessage={t("label_name_onInvalidMessage")}
                    maxLength={INPUT_SIZE.EXTRA_SERVICE.name}
                    onChange={handleNameChange}
                    disable={canEdite}
                    required
                    descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.input.namePlaceHolder"
                  />

                  <GetSpace top={10} />

                  <InputField
                    icon={faMoneyBill}
                    type="number"
                    value={value.toString()}
                    placeholder={t("label_value_place_holder")}
                    onInvalidMessage={t("label_value_onInvalidMessage")}
                    maxLength={selectedType[0] === valuesToggle[0] ? 10 : 5}
                    onChange={handleCoastChange}
                    disable={canEdite}
                    required
                    descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.input.valuePlaceHolder"
                  />

                  <GetSpace top={10} />

                  <InputField
                    type="text"
                    icon={faFile}
                    value={description}
                    disable={canEdite}
                    placeholder={t("label_description_place_holder")}
                    onInvalidMessage={t("label_description_onInvalidMessage")}
                    maxLength={INPUT_SIZE.EXTRA_SERVICE.description}
                    onChange={handleDescriptionChange}
                    descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.input.descriptionPlaceHolder"
                  />

                  <GetSpace top={5} />
                </>
              </section>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeExtraService}>
                  <>
                    <Button
                      label={t("buttons:button_save")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.button.save"
                    />

                    <GetSpace right={5} />
                  </>
                </CanView>

                {extraService && extraService.id !== -1 && (
                  <>
                    <Button
                      label={t("buttons:button_register")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.button.register"
                    />

                    <CanView permission={Permissions.CanEditeExtraService}>
                      {onCopy && (
                        <>
                          <GetSpace right={5} />

                          <Button
                            label={t("buttons:button_copy")}
                            typeButton="button"
                            type="primary"
                            size="medium"
                            bgColor={THEME.colors.buttonBGPlusColor}
                            onClick={onCopy}
                            descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.button.copy"
                          />
                        </>
                      )}
                    </CanView>
                  </>
                )}

                <CanView permission={Permissions.CanEditeExtraService}>
                  {extraService && extraService.id !== -1 && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_remove")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        iconColor={THEME.colors.buttonFGRemoveColor}
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() =>
                          setNewConfirmation(
                            t("general:label_title_remove"),
                            t("general:label_description_remove"),
                            async () => {
                              await handleRemove(extraService!, () => handleGetExtraService(onClonse))
                            },
                          )
                        }
                        descriptionKey="interactionDescriptions:pages.serivesDir.extraServices.extraServicePupop.button.remove"
                      />
                    </>
                  )}
                </CanView>
              </section>
            </form>

            {isRegisterEnable && extraService && (
              <ViewRegister id={extraService.id} typeEntity="ExtraServiceEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
